import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { FadeUp } from "@/components/Motion";
import { ArrowRight, Building2, Handshake, Briefcase, ShieldCheck, Mail, Mic2, Users, Clock } from "lucide-react";

const TYPES = [
  {
    key: "sbe", to: "/register/sbe",
    icon: Handshake,
    label: "Small Business Enterprise (SBE)",
    description:
      "Register to participate in the Summit and Business Matchmaking with participating Prime Suppliers. Complete your capability profile so Prime Suppliers can discover you.",
    testId: "reg-type-sbe",
    accent: "bg-black text-white",
    featured: true,
  },
  {
    key: "tl_attendee", to: "/register/tl-attendee",
    icon: Briefcase,
    label: "Travel + Leisure Team",
    description:
      "Registration for Travel + Leisure associates, procurement leaders, sourcing professionals, and invited executives.",
    testId: "reg-type-tl",
  },
  {
    key: "summit_guest", to: "/register/guest",
    icon: Users,
    label: "Summit Guest",
    description:
      "For Community Partners, invited companies, media, event support, and other invited guests of the Summit.",
    testId: "reg-type-guest",
    cta: "Register as a Summit Guest",
  },
  {
    key: "speaker", to: "/register/speaker",
    icon: Mic2,
    label: "Speaker",
    description:
      "Confirmed speakers for Educational Sessions and the Executive Procurement Forum. Register your speaker profile and session details — no payment required.",
    testId: "reg-type-speaker",
  },
  {
    key: "prime_supplier", to: "/onboarding",
    icon: Building2,
    label: "Prime Supplier",
    description:
      "Prime Supplier registration for the Travel + Leisure Strategic Supplier Summit is now closed. Existing Prime Suppliers should sign in to their dashboard to manage their registration, add representatives, or upload assets.",
    testId: "reg-type-prime",
    closed: true,
  },
];

export default function RegistrationPicker() {
  const [regClosed, setRegClosed] = useState(false);
  useEffect(() => {
    api.get("/event").then(({ data }) => setRegClosed(data.registration_open === false)).catch(() => {});
  }, []);
  return (
    <Layout>
      <section className="border-b border-[hsl(var(--line))] bg-[#0A0A0A] text-white">
        <div className="container-summit py-14 md:py-20">
          <FadeUp className="text-[11px] uppercase tracking-[0.28em] text-white/60 mb-4">
            <span className="h-px w-8 bg-[#A39171] inline-block mr-3 align-middle" />Event Registration
          </FadeUp>
          <FadeUp delay={0.05}>
            <h1 className="serif text-4xl sm:text-6xl leading-tight">Select your registration type.</h1>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-4 max-w-2xl text-white/70">
            Every participant at the Strategic Supplier Summit is registered — even complimentary attendees.
            Choose the option that fits you.
          </FadeUp>
        </div>
      </section>

      <section className="container-summit pt-10 md:pt-14">
        {/* Registration-closed banner */}
        {regClosed && (
          <FadeUp>
            <div className="max-w-3xl border border-red-300 bg-red-50 p-6 md:p-7 flex items-start gap-5 mb-6"
                 data-testid="registration-closed-banner">
              <div className="h-10 w-10 shrink-0 border border-red-300 flex items-center justify-center bg-white">
                <Clock size={16} className="text-red-600" strokeWidth={1.4} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-red-700">Registration Closed</div>
                <p className="mt-2 text-sm leading-relaxed text-zinc-700">
                  Registration for the Travel + Leisure Strategic Supplier Summit closed on
                  Thursday, September 10, 2026 at 3:00 PM ET. Already registered?{" "}
                  <Link to="/login" className="underline underline-offset-4 text-black">Sign in to your portal</Link>.
                  For assistance, email{" "}
                  <a href="mailto:info@displayamerica.com" className="underline underline-offset-4">info@displayamerica.com</a>.
                </p>
              </div>
            </div>
          </FadeUp>
        )}
        {/* Private Invitation notice — informational, not a warning */}
        <FadeUp>
          <div className="max-w-3xl border border-[#A39171]/50 bg-[#FBF9F5] p-6 md:p-7 flex items-start gap-5"
               data-testid="registration-invitation-notice">
            <div className="h-10 w-10 shrink-0 border border-[#A39171] flex items-center justify-center bg-white">
              <Mail size={16} className="text-[#A39171]" strokeWidth={1.4} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#A39171]">Private Invitation Event</div>
              <p className="mt-2 text-sm leading-relaxed text-zinc-700">
                The Travel + Leisure Strategic Supplier Summit is a private invitation-only event.
                Please select the registration type associated with the invitation you received.
              </p>
            </div>
          </div>
        </FadeUp>
      </section>

      <section className="container-summit py-10 md:py-14">
        <div className="grid gap-px bg-[hsl(var(--line))] md:grid-cols-3" data-testid="registration-picker">
          {TYPES.map((t) => {
            const isClosed = t.closed || regClosed;
            if (isClosed) {
              const isPrime = t.key === "prime_supplier";
              return (
                <div key={t.key} data-testid={t.testId}
                     className="bg-white p-8 md:p-10 flex flex-col gap-5 opacity-70 border-t md:border-t-0 md:border-l-0 relative">
                  <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-red-300 text-red-700 bg-red-50">Closed</span>
                  <div className="h-12 w-12 rounded-full flex items-center justify-center border border-zinc-300 text-zinc-400">
                    <t.icon size={20} strokeWidth={1.4} />
                  </div>
                  <div>
                    <div className="serif text-2xl text-zinc-500">{t.label}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-red-700 mt-1">Registration Closed</div>
                  </div>
                  <p className="text-sm text-zinc-600 leading-relaxed">{isPrime ? t.description : "Registration for the Summit is now closed. If you have already registered, please sign in to your portal."}</p>
                  <Link to="/login" className="mt-auto pt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-medium text-zinc-700 hover:text-black" data-testid={`${t.testId}-signin`}>
                    {isPrime ? "Existing Prime? Sign In" : "Already registered? Sign In"} <ArrowRight size={14} />
                  </Link>
                </div>
              );
            }
            return (
              <Link key={t.key} to={t.to} data-testid={t.testId}
                className={`bg-white p-8 md:p-10 flex flex-col gap-5 hover:bg-zinc-50 transition-colors group ${t.featured ? "ring-2 ring-[#A39171] ring-inset relative" : ""}`}>
                {t.featured && (
                  <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[#A39171] text-[#A39171] bg-[#FBF9F5]">Open</span>
                )}
                <div className={`h-12 w-12 rounded-full flex items-center justify-center border ${t.accent || "border-black"}`}>
                  <t.icon size={20} strokeWidth={1.4} />
                </div>
                <div>
                  <div className="serif text-2xl">{t.label}</div>
                </div>
                <p className="text-sm text-zinc-600 leading-relaxed">{t.description}</p>
                <div className="mt-auto pt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-medium">
                  {t.cta || "Continue"} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 text-xs text-zinc-500 max-w-2xl inline-flex items-start gap-2">
          <ShieldCheck size={14} className="mt-0.5 shrink-0 text-[#A39171]" strokeWidth={1.4} />
          Display America staff accounts are created internally. Questions? Email
          {" "}<a className="underline underline-offset-4" href="mailto:info@displayamerica.com">info@displayamerica.com</a>.
        </div>
      </section>
    </Layout>
  );
}
